import { useEffect, useMemo, useState } from 'react';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useFeatureFlagsReady } from './useFeatureFlagsReady';
import { FlagState } from '../types/FlagState';
import { validateFeatureFlagName } from '../constants';

export function useFeatureFlag<TValue = unknown>(featureFlag: string, defaultValue?: TValue): FlagState<TValue> {
  const client = useLDClient();
  const ready = useFeatureFlagsReady();

  validateFeatureFlagName(featureFlag);

  const flagState = client?.allFlags() ?? {};
  const [state, setState] = useState(flagState[featureFlag] ?? defaultValue ?? false);

  useEffect(() => {
    if (!client) {
      return;
    }
    const event = `change:${featureFlag}`;
    const handler = (value: unknown) => {
      setState(value ?? false);
    };

    client.on(event, handler);
    return () => client.off(event, handler);
  }, [client, featureFlag]);

  return useMemo(
    () => ({
      enabled: !!state,
      value: state,
      ready,
    }),
    [state, ready],
  );
}
